import { useState, useEffect } from "react";
import { useTranslation } from "../../i18n/i18n";
import { Plus, Minus, Info } from "lucide-react";

const RecurringForm = ({
	initialData = {},
	onRecurringChange,
	disabled = false,
	transactionDate = new Date().toISOString().split("T")[0],
	isRecurringEdit = false,
}) => {
	const { translate } = useTranslation();

	const frequencyOptions = {
		days: "form.recurringFrequency.days",
		weeks: "form.recurringFrequency.weeks",
		months: "form.recurringFrequency.months",
		years: "form.recurringFrequency.years",
	};

	const [recurringData, setRecurringData] = useState({
		isRecurring: false,
		recurringFrequency: "months",
		recurringInterval: 1,
		...initialData,
	});

	// Update data when initialData changes
	useEffect(() => {
		if (initialData && Object.keys(initialData).length > 0) {
			setRecurringData(prev => ({
				...prev,
				...initialData,
			}));
		}
	}, [initialData]);

	// Notify parent when data changes
	useEffect(() => {
		onRecurringChange?.(recurringData);
	}, [recurringData, onRecurringChange]);

	const handleChange = (field, value) => {
		setRecurringData(prev => ({
			...prev,
			[field]: value,
		}));
	};

	// Function to adapt the recurrence summary to gender and number
	const getRecurringSummaryText = () => {
		if (!recurringData.isRecurring) return "";

		const count = recurringData.recurringInterval;
		const unit = recurringData.recurringFrequency;

		// Adapt the determinant
		const determinantKey =
			count === 1
				? `form.recurringDeterminant.${unit}.one`
				: `form.recurringDeterminant.${unit}.other`;
		const determinant = translate(determinantKey);

		// Adapt to singular or plural
		const unitKey =
			count === 1 ? `form.recurring.${unit}` : `form.recurring.${unit}_plural`;
		const intervalAndUnit = translate(unitKey, { count });

		return translate("form.recurring.summary", {
			determinant,
			intervalAndUnit,
			date: new Date(transactionDate).toLocaleDateString(),
		});
	};

	return (
		<>
			{/* Enable Recurring - Only show if not in edit mode */}
			{!isRecurringEdit && (
				<div className="layout-row item-container">
					<label htmlFor="recurring-toggle">
						{translate("form.recurring.enable")}
					</label>
					<input
						id="recurring-toggle"
						type="checkbox"
						className="ios-switch"
						checked={recurringData.isRecurring}
						onChange={e => handleChange("isRecurring", e.target.checked)}
					/>
				</div>
			)}

			{/* Recurring Options */}
			{recurringData.isRecurring && (
				<>
					<div className="layout-row">
						<div className="layout-col-1-2">
							<label>{translate("form.recurring.frequency")}</label>
							<div className="select-wrapper">
								<select
									className="custom-select"
									value={recurringData.recurringFrequency}
									onChange={e =>
										handleChange("recurringFrequency", e.target.value)
									}
									disabled={disabled}
								>
									{Object.entries(frequencyOptions).map(([key, labelKey]) => (
										<option key={key} value={key}>
											{translate(labelKey)}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className="layout-col-1-2">
							<label htmlFor="recurring-interval">
								{translate("form.recurring.interval")}
							</label>
							<div className="interval-box">
								<button
									type="button"
									onClick={() =>
										handleChange(
											"recurringInterval",
											Math.max(
												1,
												(parseInt(recurringData.recurringInterval) || 1) - 1
											)
										)
									}
									aria-label={translate("form.recurring.decrement")}
									className="ios-button"
									disabled={recurringData.recurringInterval <= 1 || disabled}
								>
									<Minus size={18} />
								</button>
								<input
									id="recurring-interval"
									className="field"
									type="number"
									value={
										recurringData.recurringInterval === ""
											? ""
											: recurringData.recurringInterval
									}
									onChange={e => {
										const value = e.target.value;
										handleChange(
											"recurringInterval",
											value === "" ? "" : parseInt(value)
										);
									}}
									min="1"
									max="31"
									inputMode="numeric"
									aria-label={translate("form.recurring.interval")}
									disabled={disabled}
								/>
								<button
									type="button"
									onClick={() =>
										handleChange(
											"recurringInterval",
											Math.min(
												31,
												(parseInt(recurringData.recurringInterval) || 1) + 1
											)
										)
									}
									aria-label={translate("form.recurring.increment")}
									className="ios-button"
									disabled={recurringData.recurringInterval >= 31 || disabled}
								>
									<Plus size={18} />
								</button>
							</div>
						</div>
					</div>
					<div className="layout-row">
						<div className="layout-col-full">
							<p className="info-card">
								<Info size={18} />
								{getRecurringSummaryText()}
							</p>
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default RecurringForm;
